import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaFilter, FaSort, FaSortAmountDown, FaSortAmountUp, FaTimes } from 'react-icons/fa';
import './SearchFilters.css';

const SearchFilters = () => {
    // Load products from localStorage (shared with ProductManagement)
    const [products] = useState(() => {
        const savedProducts = localStorage.getItem('products');
        return savedProducts ? JSON.parse(savedProducts) : [];
    });

    // State for search and filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [activeFilters, setActiveFilters] = useState({
        category: '',
        minPrice: '',
        maxPrice: '',
        inStock: false
    });
    
    // State for sorting
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // Get unique categories from products
    const categories = [...new Set(products.map(product => product.category))];
    
    // Get min and max prices from products
    // const prices = products.map(product => product.price);
    // const minProductPrice = prices.length > 0 ? Math.min(...prices) : 0;
    // const maxProductPrice = prices.length > 0 ? Math.max(...prices) : 1000;

    // Apply all filters and sorting
    const applyFiltersAndSort = useCallback(() => {
        let results = [...products];
        
        // Apply search term filter
        if (searchTerm) {
            results = results.filter(product => 
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.category.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Apply category filter
        if (activeFilters.category) {
            results = results.filter(product => product.category === activeFilters.category);
        }
        
        // Apply price range filter
        if (activeFilters.minPrice !== '') {
            results = results.filter(product => product.price >= parseFloat(activeFilters.minPrice));
        }
        
        if (activeFilters.maxPrice !== '') {
            results = results.filter(product => product.price <= parseFloat(activeFilters.maxPrice));
        }
        
        // Apply in-stock filter
        if (activeFilters.inStock) {
            results = results.filter(product => product.stock > 0);
        }
        
        // Apply sorting
        results.sort((a, b) => {
            let comparison = 0;
            
            switch (sortField) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'price':
                    comparison = a.price - b.price;
                    break;
                case 'category':
                    comparison = a.category.localeCompare(b.category);
                    break;
                case 'stock':
                    comparison = a.stock - b.stock;
                    break;
                default:
                    comparison = 0;
            }
            
            return sortDirection === 'asc' ? comparison : -comparison;
        });
        
        setFilteredProducts(results);
    }, [searchTerm, activeFilters, sortField, sortDirection, products]);

    // Apply filters and sorting when dependencies change
    useEffect(() => {
        applyFiltersAndSort();
    }, [applyFiltersAndSort]);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setActiveFilters({
            ...activeFilters,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setActiveFilters({
            category: '',
            minPrice: '',
            maxPrice: '',
            inStock: false
        });
        setSortField('name');
        setSortDirection('asc');
    };

    // Toggle sort direction
    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Helper function to format image URLs
    const formatImageUrl = (imageUrl) => {
        const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
        
        try {
            if (!imageUrl) return defaultPlaceholder;

            // If it's already a full URL, return it
            if (imageUrl.startsWith('http')) {
                return imageUrl;
            }

            // Clean the URL
            const cleanUrl = imageUrl
                .replace(/['"]/g, '')
                .replace(/;/g, '')
                .replace(/^\/+/, '')
                .replace(/\/+/g, '/')
                .trim();

            let filename = cleanUrl;
            
            const patterns = [
                /^uploads\/products\/(.+)$/,
                /^products\/(.+)$/,
                /^app\/uploads\/products\/(.+)$/,
                /^(.+)$/
            ];

            for (const pattern of patterns) {
                const match = cleanUrl.match(pattern);
                if (match && match[1]) {
                    filename = match[1];
                    break;
                }
            }

            filename = filename
                .replace(/[^\w\-.]/g, '')
                .replace(/\.+/g, '.')
                .toLowerCase();

            if (!filename || filename.length < 5) return defaultPlaceholder;

            // Use the current origin for the image URL
            const origin = window.location.origin;
            const finalUrl = `${origin}/uploads/products/${filename}`;

            try {
                new URL(finalUrl);
                return finalUrl;
            } catch {
                return defaultPlaceholder;
            }
        } catch (error) {
            console.error('Error formatting image URL:', error);
            return defaultPlaceholder;
        }
    };

    const handleImageError = (event) => {
        const img = event.currentTarget;
        const defaultPlaceholder = 'https://placehold.co/400x400?text=No+Image';
        if (!img.src.includes('placehold.co')) {
            img.src = defaultPlaceholder;
        }
    };

    // Use this component for product images
    const ProductImage = ({ src, alt, className }) => {
        const imageUrl = formatImageUrl(src);
        const isBackendUrl = imageUrl.includes(process.env.REACT_APP_API_URL) || imageUrl.includes('igqds3armu.us-east-1.awsapprunner.com');
        
        return (
            <img
                src={imageUrl}
                alt={alt || 'Product'}
                className={`${className || ''} ${!src ? 'image-load-failed' : ''}`}
                onError={handleImageError}
                loading="lazy"
                crossOrigin={isBackendUrl ? "use-credentials" : "anonymous"}
                referrerPolicy="no-referrer"
                style={{ imageRendering: 'auto' }}
            />
        );
    };

    return (
        <div className="search-filters-container">
            <h1><FaSearch className="icon-space" /> Advanced Search & Filters</h1>
            
            <div className="search-filters-content">
                <div className="filters-panel">
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <input 
                            type="text" 
                            placeholder="Search products..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <div className="filter-section">
                        <h3><FaFilter className="icon-space" /> Filters</h3>
                        
                        <div className="filter-group">
                            <label>Category</label>
                            <select 
                                name="category" 
                                value={activeFilters.category}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Categories</option>
                                {categories.map((category, index) => (
                                    <option key={index} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Price Range</label>
                            <div className="price-range">
                                <input 
                                    type="number" 
                                    name="minPrice" 
                                    placeholder="Min" 
                                    value={activeFilters.minPrice}
                                    onChange={handleFilterChange}
                                    min={0}
                                    step="0.01"
                                />
                                <span>to</span>
                                <input 
                                    type="number" 
                                    name="maxPrice" 
                                    placeholder="Max" 
                                    value={activeFilters.maxPrice}
                                    onChange={handleFilterChange}
                                    min={0}
                                    step="0.01"
                                />
                            </div>
                        </div>
                        
                        <div className="filter-group checkbox-group">
                            <label>
                                <input 
                                    type="checkbox" 
                                    name="inStock" 
                                    checked={activeFilters.inStock}
                                    onChange={handleFilterChange}
                                />
                                In Stock Only
                            </label>
                        </div>
                        
                        <button className="btn-clear-filters" onClick={clearFilters}>
                            <FaTimes /> Clear Filters
                        </button>
                    </div>
                    
                    <div className="filter-section">
                        <h3><FaSort className="icon-space" /> Sort By</h3>
                        
                        <div className="sort-options">
                            <button 
                                className={`sort-option ${sortField === 'name' ? 'active' : ''}`}
                                onClick={() => toggleSort('name')}
                            >
                                Name
                                {sortField === 'name' && (
                                    sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                )}
                            </button>
                            
                            <button 
                                className={`sort-option ${sortField === 'price' ? 'active' : ''}`}
                                onClick={() => toggleSort('price')}
                            >
                                Price
                                {sortField === 'price' && (
                                    sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                )}
                            </button>
                            
                            <button 
                                className={`sort-option ${sortField === 'category' ? 'active' : ''}`}
                                onClick={() => toggleSort('category')}
                            >
                                Category
                                {sortField === 'category' && (
                                    sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                )}
                            </button>
                            
                            <button 
                                className={`sort-option ${sortField === 'stock' ? 'active' : ''}`}
                                onClick={() => toggleSort('stock')}
                            >
                                Stock
                                {sortField === 'stock' && (
                                    sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className="results-panel">
                    <div className="results-header">
                        <h3>Results ({filteredProducts.length} products)</h3>
                    </div>
                    
                    {filteredProducts.length > 0 ? (
                        <div className="product-grid">
                            {filteredProducts.map((product) => (
                                <div key={product.id} className="product-card">
                                    <div className="product-image">
                                        <ProductImage 
                                            src={product.imageUrl} 
                                            alt={product.name} 
                                            className="product-image"
                                        />
                                    </div>
                                    <div className="product-details">
                                        <h4 className="product-name">{product.name}</h4>
                                        <div className="product-category">{product.category}</div>
                                        <div className="product-price">${product.price.toFixed(2)}</div>
                                        <div className={`product-stock ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                            {product.stock > 0 ? `In Stock (${product.stock})` : 'Out of Stock'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-results">
                            <p>No products found matching your criteria.</p>
                            <button className="btn-clear-filters" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchFilters; 